import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { STATUSES, STATUS_COLORS, PRIORITY_COLORS, STATUS_CARD_COLORS, STATUS_ACCENT, STATUS_HEX } from "@/lib/constants";
import { slaLevelFor, SLA_WARNING_DAYS, SLA_CRITICAL_DAYS } from "@/lib/sla";
import { shortDate } from "@/lib/date";
import { Clock, AlertTriangle, Ghost, Calendar as CalIcon } from "lucide-react";
import LeadDetail from "@/pages/LeadDetail";
import { useAuth } from "@/context/AuthContext";

const SLA_BADGE = {
  ok:       { cls: "bg-slate-100 text-slate-500 border-slate-200",  Icon: Clock,          label: "in stage" },
  warn:     { cls: "bg-amber-100 text-amber-800 border-amber-300",  Icon: Clock,          label: "in stage" },
  critical: { cls: "bg-rose-100 text-rose-700 border-rose-300",     Icon: AlertTriangle,  label: "SLA breach" },
};

export default function Kanban() {
  const { user } = useAuth();
  const isAdmin = user?.role === "superadmin";
  const [leads, setLeads] = useState([]);
  const [members, setMembers] = useState([]);
  const [dragging, setDragging] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = async () => {
    const { data } = await api.get("/leads");
    setLeads(data);
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (isAdmin) api.get("/users").then((r) => setMembers(r.data)).catch(() => {});
  }, [isAdmin]);

  const onDrop = async (status) => {
    if (!dragging) return;
    const lead = dragging;
    setDragging(null);
    if (lead.status === status) return;
    // optimistic
    setLeads((ls) => ls.map((l) => (l.id === lead.id ? { ...l, status } : l)));
    try {
      await api.patch(`/leads/${lead.id}`, { status });
      toast.success(`Moved "${lead.name}" to ${status}`);
    } catch (e) {
      toast.error(formatApiError(e));
      load();
    }
  };

  return (
    <div className="space-y-6" data-testid="kanban-page">
      <header>
        <p className="text-xs uppercase tracking-[0.3em] text-slate-500">Pipeline</p>
        <h1 className="text-base font-semibold font-semibold mt-2">Kanban Board</h1>
        <p className="text-slate-600 mt-2">Drag cards across stages to update status.</p>
        <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
          <span className="uppercase tracking-widest">SLA</span>
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border bg-amber-50 border-amber-200 text-amber-800">
            <Clock className="w-3 h-3" /> ≥ {SLA_WARNING_DAYS}d
          </span>
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border bg-rose-50 border-rose-200 text-rose-700">
            <AlertTriangle className="w-3 h-3" /> ≥ {SLA_CRITICAL_DAYS}d
          </span>
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border bg-fuchsia-50 border-fuchsia-200 text-fuchsia-700">
            <Ghost className="w-3 h-3" /> Stale
          </span>
        </div>
      </header>

      <div className="flex gap-4 overflow-x-auto pb-4">
        {STATUSES.map((s) => {
          const items = leads.filter((l) => l.status === s);
          return (
            <div
              key={s}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onDrop(s)}
              className="solid-panel p-3 min-w-[280px] w-72 flex-shrink-0 max-h-[calc(100vh-220px)] flex flex-col relative overflow-hidden"
              data-testid={`kanban-col-${s}`}
            >
              <span
                className="absolute left-0 right-0 top-0 h-1"
                style={{ background: STATUS_HEX[s] || "#1D63ED" }}
                aria-hidden="true"
              />
              <div className="flex items-center justify-between mb-3 px-2">
                <div className="flex items-center gap-2">
                  <span className={STATUS_COLORS[s]}>{s}</span>
                  <span className="text-xs text-slate-500">{items.length}</span>
                </div>
              </div>
              <div className="space-y-2 overflow-y-auto pr-1 flex-1">
                {items.map((l) => {
                  const { level, days } = slaLevelFor(l);
                  const sla = SLA_BADGE[level];
                  const SlaIcon = sla.Icon;
                  return (
                    <div
                      key={l.id}
                      draggable
                      onDragStart={() => setDragging(l)}
                      onClick={() => setDetail(l)}
                      className={`kanban-card relative overflow-hidden rounded-xl p-3 pl-4 border cursor-pointer active:cursor-grabbing transition-colors duration-300 ${STATUS_CARD_COLORS[l.status] || STATUS_CARD_COLORS.New}`}
                      data-testid={`kanban-card-${l.id}`}
                    >
                      <span className={`absolute left-0 top-0 bottom-0 w-1 ${STATUS_ACCENT[l.status] || STATUS_ACCENT.New}`} aria-hidden="true" />
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <p className="font-medium text-sm leading-tight text-slate-900">{l.name}</p>
                        <span className={PRIORITY_COLORS[l.priority]}>{l.priority}</span>
                      </div>
                      {l.company && <p className="text-xs text-slate-600 mb-1">{l.company}</p>}
                      {l.budget > 0 && <p className="text-xs font-medium text-emerald-700">₹{Number(l.budget).toLocaleString()}</p>}
                      <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                        <span
                          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] ${sla.cls}`}
                          title={`${days} day${days === 1 ? "" : "s"} in ${l.status}`}
                          data-testid={`sla-badge-${l.id}`}
                        >
                          <SlaIcon className="w-3 h-3" />
                          {days}d {sla.label}
                        </span>
                        {l.is_stale && (
                          <span
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border bg-fuchsia-50 border-fuchsia-200 text-fuchsia-700 text-[10px]"
                            title="No activity for 14+ days"
                            data-testid={`stale-badge-${l.id}`}
                          >
                            <Ghost className="w-3 h-3" /> Stale
                          </span>
                        )}
                        {l.follow_up_date && (
                          <span className="text-[10px] text-slate-500">FU: {l.follow_up_date}</span>
                        )}
                      </div>
                      <div className="mt-1.5 pt-1.5 border-t border-slate-200/60 flex items-center gap-1 text-[10px] text-slate-500" data-testid={`kanban-added-${l.id}`}>
                        <CalIcon className="w-2.5 h-2.5 text-slate-400" />
                        Added {shortDate(l.created_at)}
                      </div>
                    </div>
                  );
                })}
                {items.length === 0 && (
                  <div className="text-xs text-slate-400 italic px-2 py-6 text-center">Drop here</div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <LeadDetail lead={detail} open={!!detail} onOpenChange={(v) => !v && setDetail(null)} onChanged={load} members={members} />
    </div>
  );
}
